/**
 * Générateurs des ITEMS NATIONAUX : 6e, Résolution de problèmes, campagne 2026.
 * Même contrat que 2026/6e/automatismes.js (le modèle) : un générateur par item, sa fiche en tête
 * (lue sur la CAPTURE de l'item), ITEMS avec la bonne réponse officielle exacte, CATEGORIES, generate.
 *
 * Une variante garde l'histoire de l'item (mêmes personnages, mêmes objets), sa structure et son domaine
 * de valeurs, et recalcule sur ses propres valeurs l'erreur qui produit chaque mauvaise réponse officielle.
 * Les énoncés n'emploient jamais « il » ou « elle » pour une personne nommée : son prénom.
 * Chaque tirage est vérifié par un second calcul (throw sinon), et ses quatre choix sont distincts pour le
 * correcteur. Q15 et Q16 ont une figure SVG maison, conforme aux valeurs tirées, qui ne donne pas la réponse.
 */
import { randInt, randomChoice, makeQcm, formatNumber } from '../../../utils';
import { enLettres } from '../../lettres';

const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: null, label });

// ─── Outils communs ─────────────────────────────────────────────────────────

/** Lève une erreur quand un tirage échoue à sa vérification (second calcul, choix distincts, figure). */
const verifier = (condition, message) => {
    if (!condition) throw new Error(`Items nationaux 6e, problèmes : ${message}`);
};

/**
 * Clé de comparaison de deux choix, PLUS stricte que le correcteur (normalizeAnswer) : casse, toutes les
 * marques combinantes (accents compris), toutes les virgules, toutes les espaces. Deux choix de clés
 * différentes sont différents pour le correcteur.
 */
const cle = (s) => String(s).toLowerCase().normalize('NFD').replace(/\p{M}/gu, '')
    .replace(/,/g, '.')
    .replace(/[‘’]/g, '\'')
    .replace(/\s+/g, '');

/** Le QCM d'une variante : quatre choix, la bonne réponse en premier, distincts pour le correcteur. */
const qcm = (choix, texte, label, elements = null) => {
    verifier(choix.length === 4 && new Set(choix.map(cle)).size === 4, `choix confondus : ${choix.join(' | ')}`);
    return makeQcm(choix, choix[0], texte, elements ? { ...opts(label), elements_additionnels: elements } : opts(label));
};

/** Tire des valeurs jusqu'à ce qu'elles passent le filtre : 60 essais, puis le repli (les valeurs de l'item). */
const tirer = (tirage, valide, repli) => {
    for (let essai = 0; essai < 60; essai += 1) {
        const valeurs = tirage();
        if (valide(valeurs)) return valeurs;
    }
    verifier(valide(repli), 'repli invalide');
    return repli;
};

/** Un nombre en écriture française : virgule, espaces de milliers, sans zéro inutile (3 décimales au plus). */
const fr = (x) => {
    const [entier, decimales] = String(Math.round(x * 1000) / 1000).split('.');
    return `${formatNumber(Number(entier))}${decimales ? `,${decimales}` : ''}`;
};

/** Des centimes avec leurs deux décimales : 133 → « 1,33 ». */
const deuxDecimales = (centimes) => `${Math.floor(centimes / 100)},${String(centimes % 100).padStart(2, '0')}`;

/** Un montant de choix, toujours avec ses centimes : 440 → « 4,40 € ». */
const euros = (centimes) => `${deuxDecimales(centimes)} €`;

/** Un prix d'énoncé : « 2 € » s'il tombe juste, sinon « 1,20 € ». */
const prix = (centimes) => (centimes % 100 === 0 ? `${centimes / 100} €` : euros(centimes));

/** Un nombre d'objets en lettres, accordé : un litre, deux litres. */
const enLettresAccorde = (n, nom) => (n === 1 ? `un ${nom}` : `${enLettres(n)} ${nom}s`);

/** La figure SVG de l'énoncé, en data-URL, dans les éléments additionnels. */
const figure = (svg, alt) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'medium', alt } }],
});

/**
 * Q01 : une étape, multiplicatif.
 * Item : « Lors de son anniversaire, Robin achète 15 bouteilles de jus de fruits de 0,33 L chacune.
 *   Une bouteille coûte 0,76 €. Un des calculs ci-dessous permet de trouver le nombre total de litres de
 *   jus de fruits. Lequel ? » ; 15 × 0,33 (juste), 15 + 0,33 + 0,76, 15 × 0,33 × 0,76, 15 × 0,76.
 * Domaine : 15 bouteilles, 0,33 L, 0,76 € → de 10 à 24 bouteilles ; une contenance réelle de bouteille ou
 *   de canette sous le litre (0,2 ; 0,25 ; 0,33 ; 0,5 ; 0,75 L) ; un prix sous l'euro, aux centimes non
 *   ronds comme 0,76 €, cohérent avec la contenance et jamais égal à elle.
 * Erreurs : 15 + 0,33 + 0,76, toutes les données additionnées ; 15 × 0,33 × 0,76, toutes les données
 *   multipliées, le prix compris (donnée inutile) ; 15 × 0,76, le prix pris pour la contenance.
 */
const L01 = 'Une étape, multiplicatif';
const CONTENANCES_Q01 = [ // contenance en centilitres, prix d'une bouteille en centimes
    { cl: 20, min: 25, max: 59 },
    { cl: 25, min: 29, max: 69 },
    { cl: 33, min: 39, max: 89 },
    { cl: 50, min: 49, max: 99 },
    { cl: 75, min: 69, max: 99 },
];
/** La valeur d'un calcul écrit « a + b + c » ou « a × b × c ». */
const valeurCalcul = (calcul) => (calcul.includes('+')
    ? calcul.split(' + ').reduce((s, t) => s + parseFloat(t.replace(',', '.')), 0)
    : calcul.split(' × ').reduce((p, t) => p * parseFloat(t.replace(',', '.')), 1));
const genQ01 = () => {
    const n = randInt(10, 24);
    const { cl, min, max } = randomChoice(CONTENANCES_Q01);
    const centimes = tirer(() => randInt(min, max), (p) => p % 10 !== 0 && p !== cl, 76);
    const c = fr(cl / 100);
    const p = fr(centimes / 100);
    const choix = [`${n} × ${c}`, `${n} + ${c} + ${p}`, `${n} × ${c} × ${p}`, `${n} × ${p}`];
    // second calcul : les litres bouteille par bouteille ; seul le calcul juste les donne
    let centilitres = 0;
    for (let i = 0; i < n; i += 1) centilitres += cl;
    choix.forEach((calcul, i) => {
        verifier((Math.abs(valeurCalcul(calcul) * 100 - centilitres) < 1e-6) === (i === 0), `Q01 ${calcul}`);
    });
    return qcm(choix, `<p>Lors de son anniversaire, Robin achète ${n} bouteilles de jus de fruits de ${c} L chacune.</p>`
        + `<p>Une bouteille coûte ${p} €.</p>`
        + '<p>Un des calculs ci-dessous permet de trouver le nombre total de litres de jus de fruits.</p>'
        + '<p><b>Lequel ?</b></p>', L01);
};

/**
 * Q02 : plusieurs étapes, mixte : additif, multiplicatif.
 * Item : « À la boulangerie, Kim a acheté 3 croissants à 1,20 € l'un et un pain aux raisins à 2 €.
 *   Elle donne 10 €. Combien va-t-on lui rendre ? » ; 4,40 € (juste), 5,60 €, 3,20 €, 6,80 €.
 * Domaine : 3 croissants à 1,20 €, un pain à 2 €, 10 € → de 2 à 5 croissants de 0,90 € à 1,40 € (dizaines
 *   de centimes, jamais un euro rond) ; un pain aux raisins de 1,50 € à 2,50 € (dizaines de centimes) ;
 *   10 € donnés ; aucun des quatre montants ne tombe sur un euro rond, comme dans l'item ; le rendu n'est
 *   jamais l'un des deux prix de l'énoncé.
 * Erreurs : 5,60 €, la dépense donnée pour le rendu, soustraction oubliée ; 6,80 € = 10 − (1,20 + 2), un
 *   seul croissant compté ; 3,20 € = 1,20 + 2, un seul croissant compté et la soustraction oubliée.
 * Le « Elle » de l'item devient le prénom.
 */
const L02 = 'Plusieurs étapes, mixte : additif, multiplicatif';
const genQ02 = () => {
    const { n, p, q } = tirer(
        () => ({ n: randInt(2, 5), p: randomChoice([90, 110, 120, 130, 140]), q: randInt(15, 25) * 10 }),
        (t) => {
            const depense = t.n * t.p + t.q;
            return depense <= 950 && depense % 100 !== 0 && (t.p + t.q) % 100 !== 0 && depense + t.p + t.q !== 1000
                && 1000 - depense !== t.p && 1000 - depense !== t.q;
        },
        { n: 3, p: 120, q: 200 },
    );
    const depense = n * p + q;
    const rendu = 1000 - depense;
    // second calcul : la dépense article par article, puis rendu + dépense = 10 €
    let total = q;
    for (let i = 0; i < n; i += 1) total += p;
    verifier(total === depense && rendu + total === 1000 && rendu > 0, 'Q02 rendu');
    const choix = [euros(rendu), euros(depense), euros(p + q), euros(1000 - p - q)];
    return qcm(choix, `<p>À la boulangerie, Kim a acheté ${n} croissants à ${prix(p)} l'un et un pain aux raisins à ${prix(q)}.</p>`
        + '<p>Kim donne 10 €.</p>'
        + '<p><b>Combien va-t-on lui rendre ?</b></p>', L02);
};

/**
 * Q03 : une étape, multiplicatif, proportionnalité.
 * Item : « À la boulangerie, Juliette achète 9 croissants et paie 12 euros. J'achète 3 croissants.
 *   Combien vais-je payer ? » ; 4 euros (juste), 6 euros, environ 1,33 euros, 3 euros.
 * Domaine : 9 = 3 × 3 croissants pour 12 euros, 3 croissants pour 4 euros, un croissant à 1,333… € →
 *   j'achète n croissants (3, 6 ou 7), Juliette k fois plus (k de 2 à 5, 15 croissants au plus) ; mon prix
 *   est un nombre entier d'euros ; le prix d'un croissant, de 1 € à 1,70 €, ne tombe pas juste au
 *   centime, d'où « environ » ; l'erreur additive ne tombe jamais sur les croissants de Juliette.
 * Erreurs : 6 euros = 12 − (9 − 3), modèle additif : la différence des quantités retirée au prix ;
 *   environ 1,33 euros = 12 ÷ 9, le prix d'un croissant, étape intermédiaire donnée pour réponse ;
 *   3 euros = 9 ÷ 3, le rapport des quantités donné pour prix, étape intermédiaire de l'autre procédure.
 *   Dans l'item, 3 est aussi le nombre de croissants que j'achète ; les deux lectures se séparent hors de
 *   9 et 3, la variante garde le rapport : TRANCHÉ LE 10/10 (on garde).
 */
const L03 = 'Une étape, multiplicatif, proportionnalité';
const DOMAINE_Q03 = (() => {
    const liste = [];
    [3, 6, 7].forEach((n) => {
        for (let k = 2; k <= 5; k += 1) {
            for (let a = 2; a <= 15; a += 1) { // a : mon prix, en euros
                const unite = (a * 100) / n; // le prix d'un croissant, en centimes
                const additif = k * a - (k * n - n);
                if (n * k <= 15 && unite >= 100 && unite <= 170 && (a * 100) % n !== 0
                    && new Set([a, additif, k]).size === 3 && additif > 0 && additif !== n * k) liste.push({ n, k, a });
            }
        }
    });
    return liste;
})();
const genQ03 = () => {
    const { n, k, a } = randomChoice(DOMAINE_Q03);
    const quantite = n * k;
    const paye = a * k;
    const additif = paye - (quantite - n);
    // second calcul : produit en croix, mon prix × ses croissants = son prix × mes croissants
    verifier(a * quantite === paye * n && (paye * 100) % quantite !== 0, 'Q03 proportionnalité');
    const unite = deuxDecimales(Math.round((paye * 100) / quantite));
    const choix = [`${a} euros`, `${additif} euros`, `environ ${unite} euros`, `${k} euros`];
    return qcm(choix, `<p>À la boulangerie, Juliette achète ${quantite} croissants et paie ${paye} euros.</p>`
        + `<p>J'achète ${n} croissants.</p>`
        + '<p><b>Combien vais-je payer ?</b></p>', L03);
};

/**
 * Q04 : « atypique ».
 * Item : « Je choisis un nombre. Si j'augmente ce nombre de 5, j'obtiens la moitié de 1000. Quel nombre
 *   ai-je choisi au départ ? » ; 495 (juste), 505, 1005, 995.
 * Domaine : la moitié de 1000, une augmentation de 5 → la moitié d'un nombre rond, de 200 à 2 000 (une
 *   centaine paire), une augmentation de 2 à 9.
 * Erreurs : 505 = 500 + 5, l'augmentation appliquée au lieu d'être défaite ; 995 = 1000 − 5, la moitié
 *   oubliée ; 1005 = 1000 + 5, la moitié oubliée et l'augmentation appliquée.
 */
const L04 = '« Atypique »';
const genQ04 = () => {
    const total = randInt(1, 10) * 200;
    const a = randInt(2, 9);
    const moitie = total / 2;
    const depart = moitie - a;
    // second calcul : on refait le chemin de l'énoncé, augmenter puis doubler
    verifier((depart + a) * 2 === total, 'Q04 départ');
    const choix = [depart, moitie + a, total - a, total + a].map(formatNumber);
    return qcm(choix, '<p>Je choisis un nombre.</p>'
        + `<p>Si j'augmente ce nombre de ${a}, j'obtiens la moitié de ${formatNumber(total)}.</p>`
        + '<p><b>Quel nombre ai-je choisi au départ ?</b></p>', L04);
};

/**
 * Q05 : une étape, multiplicatif, proportionnalité.
 * Item : « Dans une recette, pour faire un gâteau au chocolat pour 8 personnes, il faut 4 œufs. Combien
 *   dois-je prévoir d'œufs pour 24 personnes ? » ; 12 (juste), 24, 28, 32.
 * Domaine : 8 personnes, 4 œufs, 24 = 3 × 8 personnes → p personnes de 4 à 12, k fois plus de personnes
 *   (k de 2 à 4, 40 personnes au plus) ; k + 1 œufs dans la recette, comme 4 = 3 + 1 dans l'item (voir 32) ;
 *   la réponse n'est jamais le nombre de personnes de la recette.
 * Erreurs : 24, le nombre de personnes donné pour le nombre d'œufs ; 28 = 24 + 4, les œufs de la recette
 *   ajoutés aux personnes ; 32 = 24 + 8, les deux nombres de personnes additionnés, et c'est aussi 8 × 4,
 *   les deux nombres de la recette multipliés : avec k + 1 œufs, les deux calculs donnent toujours le même
 *   nombre, la variante garde les deux lectures de l'item.
 */
const L05 = 'Une étape, multiplicatif, proportionnalité';
const DOMAINE_Q05 = (() => {
    const liste = [];
    for (let k = 2; k <= 4; k += 1) {
        [4, 5, 6, 8, 10, 12].forEach((p) => {
            if (k * p <= 40 && p !== k && p !== k + 1 && p !== k * (k + 1)) liste.push({ k, p });
        });
    }
    return liste;
})();
const genQ05 = () => {
    const { k, p } = randomChoice(DOMAINE_Q05);
    const oeufs = k + 1;
    const personnes = k * p;
    const reponse = k * oeufs;
    // second calcul : produit en croix ; et 24 + 8 = 8 × 4, les deux lectures de l'erreur
    verifier(reponse * p === oeufs * personnes && personnes + p === oeufs * p, 'Q05 proportionnalité');
    const choix = [reponse, personnes, personnes + oeufs, personnes + p].map(String);
    return qcm(choix, `<p>Dans une recette, pour faire un gâteau au chocolat pour ${p} personnes, il faut ${oeufs} œufs.</p>`
        + `<p><b>Combien dois-je prévoir d'œufs pour ${personnes} personnes ?</b></p>`, L05);
};

/**
 * Q06 : une étape, produit cartésien.
 * Item : « Dans sa commode, Kevin a 5 écharpes différentes et 3 paires de gants différentes. Combien
 *   d'assortiments différents peut-il réaliser ? » ; 15 (juste), 8, 2, 10.
 * Domaine : 5 écharpes, 3 paires de gants → de 5 à 9 écharpes, au moins 3 paires de gants (avec 2 paires,
 *   l'erreur de la « paire » donnerait la bonne réponse), au moins 2 paires de moins que d'écharpes comme
 *   dans l'item (la différence vaut 2 au moins, jamais un « 1 » trop facile à écarter) ; la différence
 *   n'est jamais le nombre de paires de l'énoncé.
 * Erreurs : 8 = 5 + 3, addition au lieu du produit ; 2 = 5 − 3, différence ; 10 = 5 × 2, la « paire » lue
 *   comme le nombre 2 à la place du nombre de paires : erreur la plus probable, TRANCHÉ LE 10/10 (on garde).
 * Le « peut-il » de l'item devient « peut réaliser Kevin ».
 */
const L06 = 'Une étape, produit cartésien';
const DOMAINE_Q06 = (() => {
    const liste = [];
    for (let echarpes = 5; echarpes <= 9; echarpes += 1) {
        for (let paires = 3; paires <= echarpes - 2; paires += 1) {
            if (echarpes !== 2 * paires) liste.push({ echarpes, paires });
        }
    }
    return liste;
})();
const genQ06 = () => {
    const { echarpes, paires } = randomChoice(DOMAINE_Q06);
    // second calcul : les assortiments comptés un par un, chaque écharpe avec chaque paire
    let assortiments = 0;
    for (let e = 0; e < echarpes; e += 1) {
        for (let g = 0; g < paires; g += 1) assortiments += 1;
    }
    verifier(assortiments === echarpes * paires, 'Q06 assortiments');
    const choix = [assortiments, echarpes + paires, echarpes - paires, echarpes * 2].map(String);
    return qcm(choix, `<p>Dans sa commode, Kevin a ${echarpes} écharpes différentes et ${paires} paires de gants différentes.</p>`
        + `<p><b>Combien d'assortiments différents peut réaliser Kevin ?</b></p>`, L06);
};

/**
 * Q07 : une étape, multiplicatif, comparaison.
 * Item : « Matthieu a trois fois moins de jeux vidéo que Julie. Matthieu a 12 jeux vidéo. Combien de jeux
 *   a Julie ? » ; 36 (juste), 15, 4, 9.
 * Domaine : trois fois moins, 12 jeux → « k fois moins », k de 2 à 5 écrit en lettres ; Matthieu a de 6 à
 *   24 jeux, un multiple de k (12 ÷ 3 tombe juste dans l'item) ; Julie en a 60 au plus ; ni 12 ÷ 3 ni
 *   12 − 3 ne tombent sur k, comme dans l'item (sinon l'erreur se lirait aussi « k repris tel quel »).
 * Erreurs : 15 = 12 + 3, comparaison additive, « 3 de plus » ; 4 = 12 ÷ 3, « fois moins » lu comme une
 *   division : le rapport appliqué dans le mauvais sens ; 9 = 12 − 3, « moins » lu comme une soustraction.
 */
const L07 = 'Une étape, multiplicatif, comparaison';
const DOMAINE_Q07 = (() => {
    const liste = [];
    for (let k = 2; k <= 5; k += 1) {
        for (let m = 2 * k; m <= 24; m += k) {
            if (m >= 6 && k * m <= 60 && m !== 2 * k && m !== k * k && new Set([k * m, m + k, m / k, m - k]).size === 4) {
                liste.push({ k, m });
            }
        }
    }
    return liste;
})();
const genQ07 = () => {
    const { k, m } = randomChoice(DOMAINE_Q07);
    const julie = k * m;
    // second calcul : Julie partage ses jeux en k parts égales, chacune vaut ceux de Matthieu
    verifier(julie % k === 0 && julie / k === m, 'Q07 comparaison');
    const choix = [julie, m + k, m / k, m - k].map(String);
    return qcm(choix, `<p>Matthieu a ${enLettres(k)} fois moins de jeux vidéo que Julie.</p>`
        + `<p>Matthieu a ${m} jeux vidéo.</p>`
        + '<p><b>Combien de jeux a Julie ?</b></p>', L07);
};

/**
 * Q08 : deux étapes, additif et multiplicatif : nombre de parts.
 * Item : « « Sam le pirate » veut partager les 50 pièces d'or de son trésor. Il donne 7 pièces à chacun de
 *   ses compagnons. Il garde les 8 dernières pièces pour lui. Combien a-t-il de compagnons ? » ;
 *   6 (juste), 5, 8, 7.
 * Domaine : 50 = 7 × 6 + 8 → un trésor rond de 30 à 100 pièces, de 4 à 9 pièces par compagnon, de 3 à 9
 *   compagnons, de 2 à 15 pièces gardées (8 > 7 dans l'item : Sam peut garder plus qu'une part) ; le
 *   nombre de compagnons, ce nombre moins 1, la part et les pièces gardées sont quatre nombres différents.
 * Erreurs : 7, la part de chacun donnée pour le nombre de compagnons ; 8, les pièces gardées données pour le
 *   nombre de compagnons ; 5 = 6 − 1, un compagnon de moins, Sam compté parmi ceux qui reçoivent une part :
 *   (50 − 8 − 7) ÷ 7 ; erreur la plus probable, TRANCHÉ LE 10/10 (on garde).
 * Les « Il » de l'item deviennent « Sam », « pour lui » disparaît.
 */
const L08 = 'Deux étapes, additif et multiplicatif : nombre de parts';
const DOMAINE_Q08 = (() => {
    const liste = [];
    for (let tresor = 30; tresor <= 100; tresor += 10) {
        for (let part = 4; part <= 9; part += 1) {
            for (let compagnons = 3; compagnons <= 9; compagnons += 1) {
                const garde = tresor - part * compagnons;
                if (garde >= 2 && garde <= 15 && new Set([compagnons, compagnons - 1, part, garde]).size === 4) {
                    liste.push({ tresor, part, compagnons });
                }
            }
        }
    }
    return liste;
})();
const genQ08 = () => {
    const { tresor, part, compagnons } = randomChoice(DOMAINE_Q08);
    const garde = tresor - part * compagnons;
    // second calcul : on distribue le trésor part par part, il reste les pièces gardées
    let reste = tresor;
    let parts = 0;
    while (reste - part >= garde) {
        reste -= part;
        parts += 1;
    }
    verifier(parts === compagnons && reste === garde, 'Q08 partage');
    const choix = [compagnons, compagnons - 1, garde, part].map(String);
    return qcm(choix, `<p>« Sam le pirate » veut partager les ${tresor} pièces d'or de son trésor.</p>`
        + `<p>Sam donne ${part} pièces à chacun de ses compagnons.</p>`
        + `<p>Sam garde les ${garde} dernières pièces.</p>`
        + '<p><b>Combien de compagnons a Sam ?</b></p>', L08);
};

/**
 * Q09 : plusieurs étapes, multiplicatif, valeur du tout.
 * Item : « Un centre de vacances propose des séjours à 50 euros par personne et par jour. Une famille de
 *   trois personnes y passe sept jours. Combien lui coutera ce séjour ? » ; 1 050 euros (juste),
 *   350 euros, 500 euros, 150 euros. La capture écrit « coutera », orthographe rectifiée, comme la variante.
 * Domaine : 50 euros, trois personnes, sept jours (en lettres) → de 30 à 80 euros (dizaines), de trois à
 *   six personnes, de trois à dix jours, jamais autant de personnes que de jours.
 * Erreurs : 350 = 50 × 7, le nombre de personnes oublié ; 150 = 50 × 3, le nombre de jours oublié ;
 *   500 = 50 × (3 + 7), personnes et jours additionnés au lieu d'être multipliés.
 */
const L09 = 'Plusieurs étapes, multiplicatif, valeur du tout';
const genQ09 = () => {
    const { tarif, personnes, jours } = tirer(
        () => ({ tarif: randInt(3, 8) * 10, personnes: randInt(3, 6), jours: randInt(3, 10) }),
        (t) => t.personnes !== t.jours,
        { tarif: 50, personnes: 3, jours: 7 },
    );
    // second calcul : le séjour jour par jour, personne par personne
    let total = 0;
    for (let j = 0; j < jours; j += 1) {
        for (let p = 0; p < personnes; p += 1) total += tarif;
    }
    verifier(total === tarif * personnes * jours, 'Q09 séjour');
    const choix = [total, tarif * jours, tarif * (personnes + jours), tarif * personnes].map((v) => `${formatNumber(v)} euros`);
    return qcm(choix, `<p>Un centre de vacances propose des séjours à ${tarif} euros par personne et par jour.</p>`
        + `<p>Une famille de ${enLettres(personnes)} personnes y passe ${enLettres(jours)} jours.</p>`
        + '<p><b>Combien lui coutera ce séjour ?</b></p>', L09);
};

/**
 * Q10 : deux étapes, additif, comparaison.
 * Item : « Carl et Lucie courent autour d'un stade. Ils courent à la même vitesse, mais Carl a commencé à
 *   courir avant Lucie. Quand Carl a parcouru 6 tours, Lucie a parcouru 2 tours. Combien de tours aura
 *   parcourus Carl quand Lucie en aura parcouru 4 ? » ; 8 (juste), 10, 12, 6.
 * Domaine : Lucie 2 tours, Carl 6, l'écart 4, Lucie en fera 4 → Lucie l tours, Carl l + e, puis Lucie
 *   e tours : comme dans l'item, le nombre demandé est l'écart (voir 10 et 6) ; l < e ; l divise e × e,
 *   pour que le modèle multiplicatif tombe juste comme 6 ÷ 2 = 3 ; Carl a fait 15 tours au plus.
 * Erreurs : 12 = 4 × (6 ÷ 2), modèle multiplicatif, Carl court 3 fois plus que Lucie ; 10 = 6 + 4, les
 *   4 tours de Lucie ajoutés aux 6 de Carl, qui vaut aussi 6 + l'écart ; 6, les tours de Carl repris tels
 *   quels, qui vaut aussi 2 + 4, les deux nombres de Lucie additionnés. Lucie à e tours garde à chaque
 *   erreur ses deux lectures.
 * Le « Ils » de l'item disparaît : une seule phrase avec les deux prénoms.
 */
const L10 = 'Deux étapes, additif, comparaison';
const DOMAINE_Q10 = (() => {
    const liste = [];
    for (let lucie = 1; lucie <= 5; lucie += 1) {
        for (let ecart = lucie + 1; ecart <= 10; ecart += 1) {
            const multiplicatif = ((lucie + ecart) * ecart) / lucie;
            if ((ecart * ecart) % lucie === 0 && lucie + ecart <= 15 && multiplicatif <= 40) liste.push({ lucie, ecart });
        }
    }
    return liste;
})();
const genQ10 = () => {
    const { lucie, ecart } = randomChoice(DOMAINE_Q10);
    const carl = lucie + ecart;
    const reponse = ecart + ecart;
    // second calcul : même vitesse, l'écart entre Carl et Lucie ne change pas
    verifier(reponse - ecart === carl - lucie, 'Q10 écart');
    const choix = [reponse, carl + ecart, (carl * ecart) / lucie, carl].map(String);
    return qcm(choix, '<p>Carl et Lucie courent autour d\'un stade à la même vitesse, mais Carl a commencé à courir avant Lucie.</p>'
        + `<p>Quand Carl a parcouru ${carl} tours, Lucie a parcouru ${lucie} tour${lucie > 1 ? 's' : ''}.</p>`
        + `<p><b>Combien de tours aura parcourus Carl quand Lucie en aura parcouru ${ecart} ?</b></p>`, L10);
};

/**
 * Q11 : une étape, multiplicatif, proportionnalité.
 * Item : « Sur une carte, 1 cm représente 4 km dans la réalité. Trouver la distance dans la réalité d'un
 *   segment de 10 cm sur le plan. » ; 40 km (juste), 0,4 km, 4 km, 400 km.
 * Domaine : 1 cm pour 4 km, un segment de 10 cm → 1 cm pour 2 à 50 km (échelles usuelles), toujours
 *   10 cm : les trois erreurs sont des décalages autour de × 10. Jamais 1 cm pour 10 km : 10 ÷ 10 = 1 km
 *   retomberait sur le « 1 cm » de l'énoncé.
 * Erreurs : 0,4 km = 4 ÷ 10, division au lieu de la multiplication ; 4 km, l'échelle reprise telle quelle ;
 *   400 km = 4 × 100, un zéro de trop.
 * « Sur le plan » devient « sur la carte », le mot de la première phrase (mode auto, TRANCHÉ LE 10/10 (on garde)).
 */
const L11 = 'Une étape, multiplicatif, proportionnalité';
const ECHELLES_Q11 = [2, 3, 4, 5, 6, 7, 8, 9, 12, 15, 20, 25, 30, 40, 50];
const genQ11 = () => {
    const km = randomChoice(ECHELLES_Q11);
    // second calcul : centimètre par centimètre
    let reel = 0;
    for (let cm = 0; cm < 10; cm += 1) reel += km;
    verifier(reel === km * 10, 'Q11 échelle');
    const choix = [reel, km / 10, km, km * 100].map((v) => `${fr(v)} km`);
    return qcm(choix, `<p>Sur une carte, 1 cm représente ${km} km dans la réalité.</p>`
        + '<p><b>Trouver la distance dans la réalité d\'un segment de 10 cm sur la carte.</b></p>', L11);
};

/**
 * Q12 : une étape, multiplicatif, produit de grandeurs, proportionnalité.
 * Item : « Une voiture roule à vitesse constante. Elle parcourt 80 km en une heure. Quelle distance
 *   parcourt-elle en un quart d'heure ? » ; 20 km (juste), 40 km, 60 km, 80 km.
 * Domaine : 80 km en une heure → de 40 à 120 km en une heure, un multiple de 4 (le quart tombe juste).
 * Erreurs : 40 km = 80 ÷ 2, le quart d'heure pris pour une demi-heure ; 60 km = 80 − 20, le quart retiré
 *   au lieu d'être pris (trois quarts d'heure) ; 80 km, la distance en une heure reprise telle quelle.
 */
const L12 = 'Une étape, multiplicatif, produit de grandeurs, proportionnalité';
const genQ12 = () => {
    const enUneHeure = randInt(10, 30) * 4;
    const quart = enUneHeure / 4;
    // second calcul : un quart d'heure, c'est 15 minutes sur 60
    verifier((enUneHeure * 15) / 60 === quart && Number.isInteger(quart), 'Q12 quart d\'heure');
    const choix = [quart, enUneHeure / 2, enUneHeure - quart, enUneHeure].map((v) => `${v} km`);
    return qcm(choix, `<p>Une voiture roule à vitesse constante. Elle parcourt ${enUneHeure} km en une heure.</p>`
        + '<p><b>Quelle distance parcourt-elle en un quart d\'heure ?</b></p>', L12);
};

/**
 * Q13 : plusieurs étapes, mixte : additif et multiplicatif.
 * Item : « Un rectangle a un périmètre de 500 m. Sa longueur mesure 150 m. Combien mesure sa largeur ? » ;
 *   100 m (juste), 350 m, 200 m, 125 m. La base écrit les choix sans unité (100, 350, 200, 125) ; la
 *   variante les écrit avec, comme la capture.
 * Domaine : 500 m, 150 m → un périmètre en centaines de mètres, de 500 à 1 000 m ; une longueur multiple
 *   de 50 m, plus grande que la largeur ; une largeur d'au moins 50 m.
 * Erreurs : 350 = 500 − 150, une seule longueur retirée, le demi-périmètre oublié ; 200 = 500 − 2 × 150,
 *   les deux longueurs retirées, la somme des deux largeurs non partagée ; 125 = 500 ÷ 4, le rectangle
 *   traité comme un carré. La longueur n'est jamais les 3/8 du périmètre (200 et 125 seraient égaux), ni
 *   son tiers (500 − 2 × 150 retomberait sur la longueur, une donnée de l'énoncé).
 */
const L13 = 'Plusieurs étapes, mixte : additif et multiplicatif';
const DOMAINE_Q13 = (() => {
    const liste = [];
    for (let perimetre = 500; perimetre <= 1000; perimetre += 100) {
        for (let longueur = 50; longueur < perimetre / 2; longueur += 50) {
            const largeur = perimetre / 2 - longueur;
            if (largeur < longueur && largeur >= 50 && longueur * 8 !== perimetre * 3 && longueur * 3 !== perimetre) {
                liste.push({ perimetre, longueur });
            }
        }
    }
    return liste;
})();
const genQ13 = () => {
    const { perimetre, longueur } = randomChoice(DOMAINE_Q13);
    const largeur = perimetre / 2 - longueur;
    // second calcul : le tour du rectangle, côté par côté
    verifier(longueur + largeur + longueur + largeur === perimetre && largeur < longueur, 'Q13 largeur');
    const choix = [largeur, perimetre - longueur, perimetre - 2 * longueur, perimetre / 4].map((v) => `${formatNumber(v)} m`);
    return qcm(choix, `<p>Un rectangle a un périmètre de ${formatNumber(perimetre)} m.</p>`
        + `<p>Sa longueur mesure ${longueur} m.</p>`
        + '<p><b>Combien mesure sa largeur ?</b></p>', L13);
};

/**
 * Q14 : une étape, multiplicatif.
 * Item : « Un paquet de 30 objets identiques pèse 12 kg. Combien pèse un objet ? » ; 0,4 kg (juste),
 *   2,5 g, 2,5 kg, 0,4 g.
 * Domaine : 30 objets, 12 kg, 0,4 kg, et 30 ÷ 12 = 2,5 → de 10 à 50 objets, un nombre entier de kg de 2 à
 *   30 ; un objet pèse 0,2 kg, 0,4 kg, 0,5 kg ou 0,8 kg : un chiffre après la virgule, et la division à
 *   l'envers tombe juste (5 ; 2,5 ; 2 ; 1,25) ; elle ne tombe jamais sur la masse du paquet.
 * Erreurs : 2,5 kg = 30 ÷ 12, la division à l'envers ; 2,5 g, la division à l'envers, en grammes ;
 *   0,4 g, le bon quotient avec la mauvaise unité.
 */
const L14 = 'Une étape, multiplicatif';
const DOMAINE_Q14 = (() => {
    const liste = [];
    [200, 400, 500, 800].forEach((grammes) => {
        for (let objets = 10; objets <= 50; objets += 1) {
            const kg = (grammes * objets) / 1000;
            if (Number.isInteger(kg) && kg >= 2 && kg <= 30 && objets !== kg * kg) liste.push({ grammes, objets });
        }
    });
    return liste;
})();
const genQ14 = () => {
    const { grammes, objets } = randomChoice(DOMAINE_Q14);
    const kg = (grammes * objets) / 1000;
    const envers = objets / kg;
    // second calcul : en grammes, les objets du paquet un par un
    let masse = 0;
    for (let i = 0; i < objets; i += 1) masse += grammes;
    verifier(masse === kg * 1000 && Math.abs(envers * grammes - 1000) < 1e-9, 'Q14 masse');
    const choix = [`${fr(grammes / 1000)} kg`, `${fr(envers)} g`, `${fr(envers)} kg`, `${fr(grammes / 1000)} g`];
    return qcm(choix, `<p>Un paquet de ${objets} objets identiques pèse ${kg} kg.</p>`
        + '<p><b>Combien pèse un objet ?</b></p>', L14);
};

/**
 * Q15 : aire, comparaison.
 * Item : « Observer les figures ci-dessous. Laquelle de ces quatre affirmations est vraie ? », deux figures
 *   sur un quadrillage ; « L'aire de la figure A est plus petite que celle de la figure B. » (juste),
 *   « L'aire de la figure A est plus grande que celle de la figure B. », « L'aire de la figure A est la
 *   même que celle de la figure B. », « On ne peut pas savoir quelle est la plus grande aire car les deux
 *   figures ne sont pas superposables. » La base abrège : aire A < aire B, aire A > aire B, aire A = aire B,
 *   On ne peut pas savoir ; la variante écrit les phrases de la capture.
 * Domaine : figure A, un rectangle de 4 × 3 carreaux privé d'un carreau au milieu d'un côté (11 carreaux) ;
 *   figure B, le même rectangle privé d'un triangle de 2 carreaux de base et 1 de haut pris sur un bord,
 *   avec un demi-carreau ajouté sur un côté (11,5 carreaux) → le même rectangle pour les deux figures, de
 *   4 × 3 à 6 × 4 ; le carreau retiré sur l'un des quatre côtés, jamais dans un coin ; le triangle et le
 *   demi-carreau placés au hasard, figure retournée ou non ; l'écart reste d'un demi-carreau. La figure aux
 *   triangles est B, ou A une fois sur deux : la bonne réponse devient alors « plus grande », et un élève qui
 *   a retenu « plus petite » en septembre ne marque pas le point par cœur (mode auto, TRANCHÉ LE 10/10 (on garde)).
 * Erreurs : « plus grande », seuls les carreaux entiers comptés, 10 pour B contre 11 pour A ; « la même »,
 *   le demi-carreau ajouté oublié, ou pris pour compenser le triangle retiré : 12 − 1 = 11 des deux côtés ;
 *   « On ne peut pas savoir », comparer deux aires exigerait de superposer les figures.
 */
const L15 = 'Aire, comparaison';
const PHRASES_Q15 = {
    plusGrande: 'L\'aire de la figure A est plus grande que celle de la figure B.',
    plusPetite: 'L\'aire de la figure A est plus petite que celle de la figure B.',
    meme: 'L\'aire de la figure A est la même que celle de la figure B.',
    pasSavoir: 'On ne peut pas savoir quelle est la plus grande aire car les deux figures ne sont pas superposables.',
};
const RECTANGLES_Q15 = [[4, 3], [5, 3], [5, 4], [6, 4]];

/** Les sommets d'un polygone du quadrillage, sans sommet répété. */
const sansDoublon = (pts) => pts.filter(([x, y], i) => {
    const [px, py] = pts[(i + pts.length - 1) % pts.length];
    return x !== px || y !== py;
});

/** L'aire d'un polygone du quadrillage en demi-carreaux (formule du lacet) : un entier. */
const aireDouble = (pts) => Math.abs(pts.reduce((s, [x, y], i) => {
    const [x2, y2] = pts[(i + 1) % pts.length];
    return s + x * y2 - x2 * y;
}, 0));

/** Le rectangle w × h privé d'un carreau entier au milieu d'un côté, jamais dans un coin. */
const figureEncoche = (w, h) => {
    const cote = randomChoice(['droite', 'gauche', 'haut', 'bas']);
    const k = randInt(1, (cote === 'haut' || cote === 'bas' ? w : h) - 2);
    if (cote === 'droite') return [[0, 0], [w, 0], [w, k], [w - 1, k], [w - 1, k + 1], [w, k + 1], [w, h], [0, h]];
    if (cote === 'gauche') return [[0, 0], [w, 0], [w, h], [0, h], [0, k + 1], [1, k + 1], [1, k], [0, k]];
    if (cote === 'haut') return [[0, 0], [k, 0], [k, 1], [k + 1, 1], [k + 1, 0], [w, 0], [w, h], [0, h]];
    return [[0, 0], [w, 0], [w, h], [k + 1, h], [k + 1, h - 1], [k, h - 1], [k, h], [0, h]];
};

/**
 * Le rectangle w × h privé d'un triangle (2 carreaux de base, 1 de haut, colonne c) sur le bas, avec un
 * demi-carreau ajouté à droite (ligne r, jamais contre le triangle) ; retourné ou non, de gauche à droite et
 * de haut en bas. Le triangle coupe deux carreaux en deux, le demi-carreau dépasse du rectangle : w × h − 2
 * carreaux entiers et trois demi-carreaux, w × h − 0,5 carreaux en tout.
 */
const figureTriangles = (w, h) => {
    const c = randInt(0, w - 2);
    const r = randInt(0, h - 2);
    let pts = [[0, 0], [w, 0], [w, r], [w + 1, r + 1], [w, r + 1], [w, h], [c + 2, h], [c + 1, h - 1], [c, h], [0, h]];
    if (Math.random() < 0.5) pts = pts.map(([x, y]) => [w + 1 - x, y]);
    if (Math.random() < 0.5) pts = pts.map(([x, y]) => [x, h - y]);
    return sansDoublon(pts);
};

/** Les deux figures sur un même quadrillage, « Figure A » à gauche, « Figure B » à droite. */
const svgQ15 = (figures, h) => {
    const u = 24;
    const bandeau = 30;
    const largeur = (pts) => Math.max(...pts.map(([x]) => x));
    const departs = [1, largeur(figures[0]) + 4];
    const colonnes = departs[1] + largeur(figures[1]) + 1;
    const lignes = h + 2;
    const W = colonnes * u;
    const H = bandeau + lignes * u;
    let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="system-ui,sans-serif">`
        + `<rect width="${W}" height="${H}" fill="#ffffff"/>`;
    for (let i = 0; i <= colonnes; i += 1) svg += `<line x1="${i * u}" y1="${bandeau}" x2="${i * u}" y2="${H}" stroke="#cbd5e1" stroke-width="1"/>`;
    for (let j = 0; j <= lignes; j += 1) svg += `<line x1="0" y1="${bandeau + j * u}" x2="${W}" y2="${bandeau + j * u}" stroke="#cbd5e1" stroke-width="1"/>`;
    figures.forEach((pts, i) => {
        const points = pts.map(([x, y]) => `${(departs[i] + x) * u},${bandeau + (y + 1) * u}`).join(' ');
        svg += `<polygon points="${points}" fill="none" stroke="#1f2937" stroke-width="2.5" stroke-linejoin="round"/>`
            + `<text x="${(departs[i] + largeur(pts) / 2) * u}" y="${bandeau - 10}" font-size="15" font-weight="700" fill="#1f2937" text-anchor="middle">Figure ${i === 0 ? 'A' : 'B'}</text>`;
    });
    return `${svg}</svg>`;
};

const genQ15 = () => {
    const [w, h] = randomChoice(RECTANGLES_Q15);
    const encoche = figureEncoche(w, h);
    const triangles = figureTriangles(w, h);
    // second calcul : l'aire au lacet contre le décompte rectangle − retiré + ajouté, en demi-carreaux
    verifier(aireDouble(encoche) === 2 * (w * h - 1) && aireDouble(triangles) === 2 * w * h - 1, 'Q15 aires');
    const trianglesEnA = Math.random() < 0.5;
    const figures = trianglesEnA ? [triangles, encoche] : [encoche, triangles];
    const [aireA, aireB] = figures.map(aireDouble);
    const juste = aireA > aireB ? PHRASES_Q15.plusGrande : PHRASES_Q15.plusPetite;
    verifier(aireA !== aireB && (aireA > aireB) === trianglesEnA, 'Q15 comparaison');
    const choix = [juste, juste === PHRASES_Q15.plusGrande ? PHRASES_Q15.plusPetite : PHRASES_Q15.plusGrande,
        PHRASES_Q15.meme, PHRASES_Q15.pasSavoir];
    return qcm(choix, '<p>Observer les figures ci-dessous.</p>{{element:0}}'
        + '<p><b>Laquelle de ces quatre affirmations est vraie ?</b></p>', L15,
    figure(svgQ15(figures, h), 'Deux figures tracées sur un quadrillage : la figure A à gauche, la figure B à droite.'));
};

/**
 * Q16 : plusieurs étapes, additif, parties-tout, comparaison.
 * Item : « Quelle est la longueur du chemin le plus court pour aller au collège ? », un schéma : une élève
 *   à gauche, le collège à droite, deux chemins, en haut 150 m, 100 m et 100 m, en bas 250 m et 150 m ;
 *   350 m (juste), 100 m, 750 m, 400 m.
 * Domaine : en haut 150 + 100 + 100 = 350 m, en bas 250 + 150 = 400 m → des tronçons multiples de 50 m,
 *   de 100 à 200 m pour les trois du haut, de 150 à 300 m pour les deux du bas ; le chemin à deux tronçons
 *   est le plus long, de 50 ou 100 m. Le schéma dessine chaque chemin à sa propre échelle : ses tronçons
 *   sont proportionnels à leurs longueurs, et les deux chemins ont la même longueur à l'œil, comme dans
 *   l'item. Le dessin ne départage pas les chemins.
 * Erreurs : 100 m, le tronçon le plus court au lieu du chemin le plus court ; 750 m, les longueurs des deux
 *   chemins additionnées ; 400 m, l'autre chemin, qui n'a que deux tronçons et paraît plus direct.
 */
const L16 = 'Plusieurs étapes, additif, parties-tout, comparaison';
const DOMAINE_Q16 = (() => {
    const liste = [];
    const hauts = [100, 150, 200];
    const bas = [150, 200, 250, 300];
    hauts.forEach((a) => hauts.forEach((b) => hauts.forEach((c) => bas.forEach((d) => bas.forEach((e) => {
        const ecart = d + e - (a + b + c);
        if (ecart === 50 || ecart === 100) liste.push({ haut: [a, b, c], bas: [d, e] });
    })))));
    return liste;
})();

const arrondi = (v) => Math.round(v * 10) / 10;

/** Le point d'intersection de deux cercles, celui du haut ou celui du bas. */
const intersection = ([x1, y1], r1, [x2, y2], r2, versLeHaut) => {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const d = Math.hypot(dx, dy);
    const a = (r1 * r1 - r2 * r2 + d * d) / (2 * d);
    const h2 = r1 * r1 - a * a;
    verifier(d > 0 && h2 > 0, 'Q16 tracé impossible');
    const h = Math.sqrt(h2);
    const mx = x1 + (a * dx) / d;
    const my = y1 + (a * dy) / d;
    const p = [mx - (h * dy) / d, my + (h * dx) / d];
    const q = [mx + (h * dy) / d, my - (h * dx) / d];
    return (p[1] < q[1]) === versLeHaut ? p : q;
};

const ELEVE_Q16 = '<g transform="translate(-16 0)"><g stroke="#1f2937" stroke-width="1">'
    + '<circle cx="60" cy="100" r="11" fill="#92400e"/>'
    + '<circle cx="46" cy="99" r="4.5" fill="#92400e"/><circle cx="74" cy="99" r="4.5" fill="#92400e"/>'
    + '<circle cx="60" cy="104" r="9" fill="#f5d0a9"/>'
    + '<path d="M54 115 H66 L75 152 H45 Z" fill="#db2777"/>'
    + '</g>'
    + '<circle cx="56.5" cy="104" r="1.2" fill="#1f2937"/><circle cx="63.5" cy="104" r="1.2" fill="#1f2937"/>'
    + '<g stroke="#f5d0a9" stroke-width="3.5" stroke-linecap="round">'
    + '<line x1="55" y1="120" x2="47" y2="138"/><line x1="65" y1="120" x2="73" y2="138"/>'
    + '<line x1="55" y1="153" x2="54" y2="172"/><line x1="65" y1="153" x2="66" y2="172"/>'
    + '</g>'
    + '<ellipse cx="52" cy="174" rx="5" ry="2.5" fill="#1f2937"/><ellipse cx="68" cy="174" rx="5" ry="2.5" fill="#1f2937"/></g>';

const COLLEGE_Q16 = (() => {
    let svg = '<rect x="418" y="100" width="92" height="92" fill="#e5e7eb" stroke="#4b5563" stroke-width="1.5"/>'
        + '<rect x="432" y="82" width="64" height="18" fill="#f8fafc" stroke="#4b5563" stroke-width="1.2"/>'
        + '<text x="464" y="95" font-size="11" text-anchor="middle" fill="#111827">Collège</text>';
    for (let ligne = 0; ligne < 3; ligne += 1) {
        for (let col = 0; col < 4; col += 1) svg += `<rect x="${426 + col * 21}" y="${108 + ligne * 20}" width="12" height="13" fill="#475569"/>`;
    }
    return `${svg}<rect x="455" y="168" width="18" height="24" fill="#94a3b8" stroke="#4b5563"/>`;
})();

/** Le schéma : l'élève, le collège, les deux chemins et leurs tronçons ; chaque chemin à sa propre échelle. */
const svgQ16 = ([a, b, c], [d, e]) => {
    const W = 520;
    const H = 250;
    const xDepart = 110;
    const xArrivee = 400;
    const yHaut = 118;
    const yBas = 148;
    const dessinee = 1.1 * (xArrivee - xDepart); // la longueur dessinée de chacun des deux chemins
    const kHaut = dessinee / (a + b + c);
    const kBas = dessinee / (d + e);
    const s1 = [xDepart, yHaut];
    const e1 = [xArrivee, yHaut];
    const p2 = [xArrivee - kHaut * c, yHaut];
    const p1 = intersection(s1, kHaut * a, p2, kHaut * b, true);
    const s2 = [xDepart, yBas];
    const e2 = [xArrivee, yBas];
    const q = intersection(s2, kBas * d, e2, kBas * e, false);
    // tronçon : début, fin, longueur réelle, côté de l'étiquette (−1 au-dessus, 1 au-dessous)
    const troncons = [[s1, p1, a, -1], [p1, p2, b, -1], [p2, e1, c, -1], [s2, q, d, 1], [q, e2, e, 1]];
    let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="system-ui,sans-serif">`
        + `<rect width="${W}" height="${H}" fill="#ffffff"/>${ELEVE_Q16}${COLLEGE_Q16}`;
    [[s1, p1, p2, e1], [s2, q, e2]].forEach((chemin) => {
        svg += `<polyline points="${chemin.map(([x, y]) => `${arrondi(x)},${arrondi(y)}`).join(' ')}" fill="none" stroke="#111827" stroke-width="1.8"/>`;
        chemin.forEach(([x, y]) => {
            verifier(x > 8 && x < W - 8 && y > 8 && y < H - 8, 'Q16 point hors du schéma');
            svg += `<path d="M${arrondi(x - 4)} ${arrondi(y - 4)}L${arrondi(x + 4)} ${arrondi(y + 4)}M${arrondi(x - 4)} ${arrondi(y + 4)}L${arrondi(x + 4)} ${arrondi(y - 4)}" stroke="#111827" stroke-width="1.6"/>`;
        });
    });
    troncons.forEach(([[x1, y1], [x2, y2], longueur, cote]) => {
        const l = Math.hypot(x2 - x1, y2 - y1);
        verifier(Math.abs(l - (cote < 0 ? kHaut : kBas) * longueur) < 1e-6, 'Q16 tronçon non proportionnel');
        const x = (x1 + x2) / 2 + (cote * -(y2 - y1) * 19) / l;
        const y = (y1 + y2) / 2 + (cote * (x2 - x1) * 19) / l;
        verifier(x > 25 && x < W - 25 && y > 12 && y < H - 8, 'Q16 étiquette hors du schéma');
        svg += `<text x="${arrondi(x)}" y="${arrondi(y + 4.5)}" font-size="13" text-anchor="middle" fill="#111827">${longueur} m</text>`;
    });
    return `${svg}</svg>`;
};

const genQ16 = () => {
    const { haut, bas } = randomChoice(DOMAINE_Q16);
    const court = haut.reduce((s, v) => s + v, 0);
    const long = bas.reduce((s, v) => s + v, 0);
    const plusPetitTroncon = Math.min(...haut, ...bas);
    // second calcul : chaque chemin refait tronçon par tronçon, en partant du collège
    verifier([...haut].reverse().reduce((s, v) => s + v, 0) === court && bas[1] + bas[0] === long && court < long, 'Q16 chemins');
    const choix = [court, plusPetitTroncon, court + long, long].map((v) => `${formatNumber(v)} m`);
    const alt = `Une élève à gauche, le collège à droite. Chemin du haut : trois tronçons de ${haut[0]} m, ${haut[1]} m et ${haut[2]} m. `
        + `Chemin du bas : deux tronçons de ${bas[0]} m et ${bas[1]} m.`;
    return qcm(choix, '<p><b>Quelle est la longueur du chemin le plus court pour aller au collège ?</b></p>{{element:0}}', L16,
        figure(svgQ16(haut, bas), alt));
};

/**
 * Q17 : une étape, multiplicatif, valeur d'une part.
 * Item : « Marie a constaté qu'il faut un litre de lait pour remplir complètement quatre verres
 *   identiques. Quelle est la contenance d'un verre ? » ; 25 cL (juste), 0,25 cL, 4 cL, 0,04 L.
 * Domaine : un litre, quatre verres (en lettres), 25 cL → un, deux ou trois litres, de deux à vingt verres,
 *   un verre de 15 à 50 cL, un nombre entier de centilitres. Avec un litre seul, trois variantes seulement
 *   (deux, quatre ou cinq verres) : deux et trois litres sont ajoutés (mode auto, TRANCHÉ LE 10/10 (on garde)).
 * Erreurs : 0,25 cL = 1 ÷ 4, le quotient en litres écrit en centilitres, conversion oubliée ; 4 cL, le
 *   nombre de verres pris pour la contenance (dans l'item, c'est aussi 4 ÷ 1, la division à l'envers ; les
 *   deux lectures se séparent hors d'un litre, la variante garde le nombre de verres : TRANCHÉ LE 10/10 (on garde)) ;
 *   0,04 L, la même erreur que 4 cL, convertie en litres.
 */
const L17 = 'Une étape, multiplicatif, valeur d\'une part';
const DOMAINE_Q17 = (() => {
    const liste = [];
    [1, 2, 3].forEach((litres) => {
        for (let verres = 2; verres <= 20; verres += 1) {
            const contenance = (100 * litres) / verres;
            if (Number.isInteger(contenance) && contenance >= 15 && contenance <= 50 && contenance !== verres) liste.push({ litres, verres });
        }
    });
    return liste;
})();
const genQ17 = () => {
    const { litres, verres } = randomChoice(DOMAINE_Q17);
    const contenance = (100 * litres) / verres;
    // second calcul : les verres remplis un par un vident le lait, en centilitres
    let lait = 100 * litres;
    for (let i = 0; i < verres; i += 1) lait -= contenance;
    verifier(lait === 0, 'Q17 contenance');
    const choix = [`${contenance} cL`, `${fr(litres / verres)} cL`, `${verres} cL`, `${fr(verres / 100)} L`];
    return qcm(choix, `<p>Marie a constaté qu'il faut ${enLettresAccorde(litres, 'litre')} de lait pour remplir complètement ${enLettres(verres)} verres identiques.</p>`
        + '<p><b>Quelle est la contenance d\'un verre ?</b></p>', L17);
};

/**
 * Q18 : plusieurs étapes, mixte : additif et multiplicatif.
 * Item : « Une douche représente en moyenne 80 L d'eau et un bain 200 L d'eau. Si Joseph prend une douche
 *   par jour au lieu d'un bain, quelle sera l'économie d'eau réalisée à la fin d'une semaine ? » ;
 *   840 L (juste), 280 L, 1 400 L, 1 960 L.
 * Domaine : 80 L, 200 L, une semaine → une douche de 50 à 100 L, un bain de 150 à 250 L (dizaines),
 *   toujours une semaine de 7 jours.
 * Erreurs : 280 L = 80 + 200, les deux volumes additionnés, ni écart ni semaine ; 1 400 L = 200 × 7, l'eau
 *   des bains de la semaine, celle des douches non retirée ; 1 960 L = (80 + 200) × 7, les volumes
 *   additionnés au lieu d'être soustraits, puis la semaine.
 */
const L18 = 'Plusieurs étapes, mixte : additif et multiplicatif';
const genQ18 = () => {
    const douche = randInt(5, 10) * 10;
    const bain = randInt(15, 25) * 10;
    const economie = (bain - douche) * 7;
    // second calcul : l'eau des bains de la semaine moins celle des douches
    verifier(bain * 7 - douche * 7 === economie, 'Q18 économie');
    const choix = [economie, douche + bain, bain * 7, (douche + bain) * 7].map((v) => `${formatNumber(v)} L`);
    return qcm(choix, `<p>Une douche représente en moyenne ${douche} L d'eau et un bain ${bain} L d'eau.</p>`
        + '<p><b>Si Joseph prend une douche par jour au lieu d\'un bain, quelle sera l\'économie d\'eau réalisée à la fin d\'une semaine ?</b></p>', L18);
};

/**
 * Q19 : deux étapes, additif.
 * Item : « Le cours de mathématiques dure 55 minutes. Aujourd'hui, le professeur a débuté par des
 *   activités mentales qui ont duré un quart d'heure. Le reste du temps, à partir de 10 h 10, a été
 *   consacré à résoudre des problèmes. À quelle heure le cours s'est-il terminé ? » ; 10 h 50 (juste),
 *   10 h 25, 11 h 20, 11 h 05.
 * Domaine : 55 minutes, un quart d'heure, 10 h 10 → un cours de 45, 50, 55 ou 60 minutes, toujours un
 *   quart d'heure d'activités mentales ; les problèmes commencent entre 8 h 05 et 15 h 25, à 5 minutes
 *   près ; aucune des heures écrites ne tombe sur une heure pile.
 * Erreurs : 10 h 25 = 10 h 10 + 15 min, le quart d'heure ajouté à l'heure de départ des problèmes ;
 *   11 h 05 = 10 h 10 + 55 min, la durée du cours comptée depuis 10 h 10, le quart d'heure oublié ;
 *   11 h 20 = 10 h 10 + 55 min + 15 min, les deux durées ajoutées.
 */
const L19 = 'Deux étapes, additif';
/** Une heure écrite « 10 h 05 », à partir des minutes depuis minuit. */
const ecrireHeure = (minutes) => `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, '0')}`;
const genQ19 = () => {
    const { duree, debut } = tirer(
        () => ({ duree: randomChoice([45, 50, 55, 60]), debut: randomChoice([8, 9, 10, 11, 13, 14, 15]) * 60 + randInt(1, 5) * 5 }),
        (t) => [t.debut, t.debut + t.duree - 15, t.debut + 15, t.debut + t.duree, t.debut + t.duree + 15].every((m) => m % 60 !== 0),
        { duree: 55, debut: 610 },
    );
    const fin = debut + duree - 15;
    // second calcul : le cours a commencé un quart d'heure avant les problèmes et a duré `duree` minutes
    verifier(debut - 15 + duree === fin && fin > debut, 'Q19 heure de fin');
    const choix = [fin, debut + 15, debut + duree + 15, debut + duree].map(ecrireHeure);
    return qcm(choix, `<p>Le cours de mathématiques dure ${duree} minutes.</p>`
        + '<p>Aujourd\'hui, le professeur a débuté par des activités mentales qui ont duré un quart d\'heure.</p>'
        + `<p>Le reste du temps, à partir de ${ecrireHeure(debut)}, a été consacré à résoudre des problèmes.</p>`
        + '<p><b>À quelle heure le cours s\'est-il terminé ?</b></p>', L19);
};

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_qcm_unique: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_qcm_unique: genQ06,
    q07_qcm_unique: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_qcm_unique: genQ10,
    q11_qcm_unique: genQ11,
    q12_qcm_unique: genQ12,
    q13_qcm_unique: genQ13,
    q14_qcm_unique: genQ14,
    q15_qcm_unique: genQ15,
    q16_qcm_unique: genQ16,
    q17_qcm_unique: genQ17,
    q18_qcm_unique: genQ18,
    q19_qcm_unique: genQ19,
};

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: '15 × 0,33' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: '4,40 €' },
    { questionNum: 'Q03', formatKey: 'q03_qcm_unique', libelle: L03, bonneReponseOfficielle: '4 euros' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: '495' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: '12' },
    { questionNum: 'Q06', formatKey: 'q06_qcm_unique', libelle: L06, bonneReponseOfficielle: '15' },
    { questionNum: 'Q07', formatKey: 'q07_qcm_unique', libelle: L07, bonneReponseOfficielle: '36' },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: '6' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: '1 050 euros' },
    { questionNum: 'Q10', formatKey: 'q10_qcm_unique', libelle: L10, bonneReponseOfficielle: '8' },
    { questionNum: 'Q11', formatKey: 'q11_qcm_unique', libelle: L11, bonneReponseOfficielle: '40 km' },
    { questionNum: 'Q12', formatKey: 'q12_qcm_unique', libelle: L12, bonneReponseOfficielle: '20 km' },
    { questionNum: 'Q13', formatKey: 'q13_qcm_unique', libelle: L13, bonneReponseOfficielle: '100' },
    { questionNum: 'Q14', formatKey: 'q14_qcm_unique', libelle: L14, bonneReponseOfficielle: '0,4 kg' },
    { questionNum: 'Q15', formatKey: 'q15_qcm_unique', libelle: L15, bonneReponseOfficielle: 'aire A < aire B' },
    { questionNum: 'Q16', formatKey: 'q16_qcm_unique', libelle: L16, bonneReponseOfficielle: '350 m' },
    { questionNum: 'Q17', formatKey: 'q17_qcm_unique', libelle: L17, bonneReponseOfficielle: '25 cL' },
    { questionNum: 'Q18', formatKey: 'q18_qcm_unique', libelle: L18, bonneReponseOfficielle: '840 L' },
    { questionNum: 'Q19', formatKey: 'q19_qcm_unique', libelle: L19, bonneReponseOfficielle: '10 h 50' },
];

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: 'QCM' }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
